import { useRef, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { updateJournalSummary } from '@/services/journalAIService';
import type { BlockType } from '@/types/journal';

/**
 * Smart journal summary update strategy
 * 
 * Rules:
 * - Add AI answer: Immediate update (new learning content)
 * - Delete note/explainer/quiz: Immediate update (meaningful content removed)
 * - Delete PDF/YouTube: Conditional update (only if had processed content)
 * - Edit note: Debounced update (30s after last edit)
 * - Add PDF/YouTube: Debounced update (10s after add, waiting for processing)
 * - Reorder blocks: No update (order doesn't affect summary)
 */

interface UpdateTrigger {
  immediate?: boolean;
  debounceMs?: number;
  skip?: boolean;  // Skip automatic update - we'll trigger explicitly
}

/**
 * Track block additions to implement smart batching
 */
let blockAdditionCounter = 0;
let lastUpdateTime = Date.now();

/**
 * Determine if and how a block change should trigger a summary update
 * 
 * Production-ready strategy:
 * - Immediate: First block, significant content (PDF/YouTube/Explainer/Quiz), deletions
 * - Batched: Notes (every 3rd note OR after 5 minutes)
 * - Debounced: Edits (30s after last change)
 */
function getUpdateStrategy(
  blockType: BlockType,
  operation: 'add' | 'edit' | 'delete',
  hasContent: boolean = true,
  metadata?: { isFirstBlock?: boolean; isAIGenerated?: boolean; totalBlocks?: number; forceImmediate?: boolean }
): UpdateTrigger | null {
  // Force immediate update when explicitly requested (e.g., after PDF analysis completes)
  if (metadata?.forceImmediate) {
    console.log('[Summary] ⚡ Force immediate update requested');
    return { immediate: true };
  }

  // Never update for empty blocks being deleted
  if (operation === 'delete' && !hasContent) {
    return null;
  }

  // ALWAYS update on first block (establishes baseline summary + auto-naming)
  if (metadata?.isFirstBlock) {
    console.log('[Summary] 🚀 First block detected - triggering IMMEDIATE update');
    return { immediate: true };
  }

  // AI-generated content is always immediate (complete, high-value)
  if (metadata?.isAIGenerated) {
    console.log('[Summary] 🤖 AI-generated content - triggering IMMEDIATE update');
    return { immediate: true };
  }

  // Significant content blocks: Different handling per type
  const significantTypes: BlockType[] = ['pdf', 'youtube', 'explainer', 'quiz'];

  if (significantTypes.includes(blockType)) {
    // PDF blocks: Don't trigger immediate on 'add' - we trigger after analysis completes
    // This prevents premature summary updates before the PDF has been analyzed
    if (blockType === 'pdf') {
      if (operation === 'delete' && hasContent) {
        return { immediate: true };
      }
      // 'add' returns skip - we call triggerUpdate explicitly after analyzePDF completes
      return { skip: true };
    }

    // Other significant types: immediate for add/delete
    if (operation === 'add' || operation === 'delete') {
      return { immediate: true };
    }
  }

  // Note blocks: Smart batching to avoid update spam
  if (blockType === 'note') {
    if (operation === 'delete' && hasContent) {
      return { immediate: true }; // Immediate on delete
    }

    if (operation === 'add') {
      // ALWAYS immediate for first block (crucial for auto-naming)
      if (metadata?.isFirstBlock) {
        return { immediate: true };
      }

      blockAdditionCounter++;
      const timeSinceLastUpdate = Date.now() - lastUpdateTime;

      // Update every 3rd note OR after 2 minutes (reduced from 5)
      if (blockAdditionCounter >= 3 || timeSinceLastUpdate > 120000) {
        blockAdditionCounter = 0;
        lastUpdateTime = Date.now();
        return { immediate: true };
      }

      // Otherwise, queue a shorter debounced update (10s instead of 60s)
      return { immediate: false, debounceMs: 10000 }; // 10 seconds
    }

    if (operation === 'edit') {
      return { immediate: false, debounceMs: 30000 }; // 30s
    }
  }

  return null;
}

/**
 * Hook for managing journal summary updates
 * 
 * Provides methods to trigger updates based on block operations,
 * with automatic debouncing and queueing.
 */
export function useJournalSummaryUpdates(journalId: string) {
  const queryClient = useQueryClient();
  const immediateQueueRef = useRef<Set<string>>(new Set());
  const debouncedTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isUpdatingRef = useRef(false);

  // Process immediate updates
  const processImmediateUpdate = useCallback(async () => {
    if (isUpdatingRef.current || immediateQueueRef.current.size === 0) {
      return;
    }

    isUpdatingRef.current = true;
    immediateQueueRef.current.clear();

    try {
      const result = await updateJournalSummary(journalId);
      console.log(`[Summary] Updated journal ${journalId}`);

      // If journal name was auto-updated, invalidate journal query to refetch
      if (result.journal_name_updated) {
        console.log(`[Summary] 🏷️  Journal auto-named: "${result.journal_name_updated}"`);
        queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
        queryClient.invalidateQueries({ queryKey: ['journals'] });  // Also update list
      }
    } catch (error) {
      console.warn('[Summary] Update failed:', error);
    } finally {
      isUpdatingRef.current = false;
    }
  }, [journalId, queryClient]);

  // Process debounced update
  const processDebouncedUpdate = useCallback(async () => {
    if (isUpdatingRef.current) {
      return;
    }

    isUpdatingRef.current = true;

    try {
      const result = await updateJournalSummary(journalId);
      console.log(`[Summary] Debounced update for journal ${journalId}`);

      // Invalidate journal query if name was updated (rare for debounced, but possible)
      if (result.journal_name_updated) {
        console.log(`[Summary] 🏷️  Journal auto-named: "${result.journal_name_updated}"`);
        queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
        queryClient.invalidateQueries({ queryKey: ['journals'] });
      }
    } catch (error) {
      console.warn('[Summary] Debounced update failed:', error);
    } finally {
      isUpdatingRef.current = false;
    }
  }, [journalId, queryClient]);

  /**
   * Trigger a summary update based on block operation
   */
  const triggerUpdate = useCallback((
    blockType: BlockType,
    operation: 'add' | 'edit' | 'delete',
    options: {
      hasContent?: boolean;
      blockId?: string;
      isFirstBlock?: boolean;
      isAIGenerated?: boolean;
      totalBlocks?: number;
      forceImmediate?: boolean;
    } = {}
  ) => {
    const { hasContent = true, blockId = 'unknown', isFirstBlock = false, isAIGenerated = false, totalBlocks = 0, forceImmediate = false } = options;

    const strategy = getUpdateStrategy(blockType, operation, hasContent, { isFirstBlock, isAIGenerated, totalBlocks, forceImmediate });

    if (!strategy) {
      console.log(`[Summary] No update needed for ${operation} on ${blockType} (total: ${totalBlocks})`);
      return;
    }

    if (strategy.skip) {
      console.log(`[Summary] Skipping automatic update for ${blockType} ${operation} - will be triggered explicitly`);
      return;
    }

    if (strategy.immediate) {
      // Queue immediate update
      immediateQueueRef.current.add(blockId);
      console.log(`[Summary] Queued immediate update for ${blockType} ${operation}`);

      // Process on next tick (allows batching multiple operations)
      setTimeout(() => {
        processImmediateUpdate();
      }, 0);
    } else if (strategy.debounceMs) {
      // Clear existing debounced timer
      if (debouncedTimerRef.current) {
        clearTimeout(debouncedTimerRef.current);
      }

      console.log(`[Summary] Scheduled debounced update for ${blockType} ${operation} (${strategy.debounceMs}ms)`);

      // Set new debounced timer
      debouncedTimerRef.current = setTimeout(() => {
        processDebouncedUpdate();
      }, strategy.debounceMs);
    }
  }, [processImmediateUpdate, processDebouncedUpdate]);

  /**
   * Cancel any pending debounced updates
   */
  const cancelPendingUpdates = useCallback(() => {
    if (debouncedTimerRef.current) {
      clearTimeout(debouncedTimerRef.current);
      debouncedTimerRef.current = null;
      console.log('[Summary] Cancelled pending debounced update');
    }
  }, []);

  /**
   * Force an immediate update (bypass strategy logic)
   */
  const forceUpdate = useCallback(() => {
    console.log('[Summary] Forcing immediate update');
    immediateQueueRef.current.add('force');
    processImmediateUpdate();
  }, [processImmediateUpdate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debouncedTimerRef.current) {
        clearTimeout(debouncedTimerRef.current);
      }
    };
  }, []);

  return {
    triggerUpdate,
    cancelPendingUpdates,
    forceUpdate,
  };
}

